import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { useRole } from '@/hooks/use-role';
import { createJobInvoice, jobInvoicesQueryKey, listJobInvoices } from '@/lib/api/invoices';
import type { PaymentMethod } from '@/lib/api/jobs';
import { formatMoney } from '@/lib/utils';

import { AddInvoiceModal } from '@/routes/invoices/add-invoice-modal';
import {
  invoicePhase,
  invoicePhaseColorClass,
  invoicePhaseLabel,
} from '@/routes/invoices/helpers';
import { InvoiceDetailModal } from '@/routes/invoices/invoice-detail-modal';
import type { NewInvoiceSubmission } from '@/routes/invoices/types';

interface Props {
  businessId: string;
  jobId: string;
  jobTitle: string;
  jobDefaultPaymentMethod?: PaymentMethod | null;
  jobDefaultPaymentMethodId?: string | null;
}

// Invoices section of the job detail modal — mirrors `job-quotes-section.tsx`.
// Self-contained: owns its list query + create mutation; the shared
// `InvoiceDetailModal` handles per-invoice edit / send / share-link / confirm.
export function JobInvoicesSection({
  businessId,
  jobId,
  jobTitle,
  jobDefaultPaymentMethod,
  jobDefaultPaymentMethodId,
}: Props) {
  const { t } = useLanguage();
  const { isOwner, hasPermission } = useRole();
  const canManage = isOwner || hasPermission('create_invoices');

  const [activeInvoiceId, setActiveInvoiceId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [autoShareOnOpen, setAutoShareOnOpen] = useState(false);

  const invoicesQuery = useQuery({
    queryKey: jobInvoicesQueryKey(businessId, jobId),
    queryFn: () => listJobInvoices(businessId, jobId),
  });

  const createMutation = useMutation({
    mutationFn: (s: NewInvoiceSubmission) =>
      createJobInvoice(businessId, jobId, {
        total_amount: s.total_amount,
        payment_method_ids: s.payment_method_ids,
        subtotal: s.subtotal,
        tax: s.tax,
        discount: s.discount,
        due_date: s.due_date,
      }),
    meta: { successMessage: t.invoiceCreated },
    onSuccess: (invoice) => {
      setIsAdding(false);
      setAutoShareOnOpen(true);
      setActiveInvoiceId(invoice.id);
    },
  });

  const invoices = invoicesQuery.data ?? [];

  return (
    <div className="px-6 py-5 border-t flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t.invoicesTitle}</h3>
        <Button
          type="button"
          size="xs"
          variant="outline"
          data-testid="job-add-invoice"
          onClick={() => setIsAdding(true)}
        >
          <PlusIcon className="w-3 h-3" />
          {t.addInvoice}
        </Button>
      </div>

      {invoicesQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.noInvoicesFound}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {invoices.map((invoice) => {
            const phase = invoicePhase(invoice);
            return (
              <li key={invoice.id}>
                <button
                  type="button"
                  onClick={() => {
                    setAutoShareOnOpen(false);
                    setActiveInvoiceId(invoice.id);
                  }}
                  data-testid={`invoice-row-${invoice.id}`}
                  className="w-full flex items-center gap-3 text-sm rounded-md px-2 py-1.5 hover:bg-muted transition-colors"
                >
                  <span className="flex-1 text-left font-medium">
                    {formatMoney(invoice.total_amount)}
                  </span>
                  <span
                    className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${invoicePhaseColorClass(phase)}`}
                  >
                    {invoicePhaseLabel(phase, t)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {isAdding && (
        <AddInvoiceModal
          businessId={businessId}
          jobId={jobId}
          jobTitle={jobTitle}
          jobDefaultPaymentMethod={jobDefaultPaymentMethod}
          jobDefaultPaymentMethodId={jobDefaultPaymentMethodId}
          onClose={() => setIsAdding(false)}
          onSave={(s) => createMutation.mutate(s)}
          isSaving={createMutation.isPending}
        />
      )}

      {activeInvoiceId && (
        <InvoiceDetailModal
          businessId={businessId}
          jobId={jobId}
          invoiceId={activeInvoiceId}
          canManage={canManage}
          autoShareLink={autoShareOnOpen}
          onClose={() => {
            setActiveInvoiceId(null);
            setAutoShareOnOpen(false);
          }}
        />
      )}
    </div>
  );
}
